
import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Select } from './ui';
import { User, UserRole } from '../types';
import { ArrowLeft, AlertCircle, Mail, CheckCircle2, ShieldCheck, Phone, Loader2 } from 'lucide-react';
import { supabase } from '../services/supabase';
import { api } from '../services/api';

declare global {
  interface Window {
    google: any;
  }
}

interface AuthProps {
  onLogin: (user: User) => void;
  onBack: () => void;
  defaultMode?: 'login' | 'signup';
  onSwitchMode: (mode: 'login' | 'signup') => void;
}

export const Auth = ({ onLogin, onBack, defaultMode = 'login', onSwitchMode }: AuthProps) => {
  const [mode, setMode] = useState<'login' | 'signup'>(defaultMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [phone, setPhone] = useState('');
  
  const googleButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMode(defaultMode);
    setError('');
  }, [defaultMode]);

  // Google Sign In Initialization with Nonce Security
  useEffect(() => {
    const initializeGoogleSignIn = async () => {
      if (!window.google) return;

      try {
        // 1. Generate a random nonce
        const nonce = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))));
        
        // 2. Hash the nonce (SHA-256)
        const encoder = new TextEncoder();
        const encodedNonce = encoder.encode(nonce);
        const hashBuffer = await crypto.subtle.digest('SHA-256', encodedNonce);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashedNonce = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

        // 3. Initialize Google Identity Services
        window.google.accounts.id.initialize({
          client_id: '692810049394-ivbcrfnlqk47gbrqu314bfr3c7fr084j.apps.googleusercontent.com',
          callback: async (response: any) => {
            setLoading(true);
            setError('');
            try {
              // 4. Pass the Google token and the RAW nonce to Supabase
              const { data, error } = await supabase.auth.signInWithIdToken({
                provider: 'google',
                token: response.credential,
                nonce: nonce, // Supabase expects the raw nonce to verify against the hashed nonce in the token
              });

              if (error) throw error;

              // Successful login/signup, fetch profile details
              // Note: If this is a new user, the Trigger in Supabase (handle_new_user)
              // should create the profile record automatically.
              const profile = await api.getCurrentUser();
              
              if (profile) {
                onLogin(profile);
              } else {
                // Short retry in case trigger has slight latency
                setTimeout(async () => {
                   const retryProfile = await api.getCurrentUser();
                   if (retryProfile) onLogin(retryProfile);
                   else setError("Profile setup incomplete. Please contact support.");
                }, 2000);
              }
            } catch (err: any) {
              console.error('Google Sign-In Error:', err);
              setError(err.message || "Google Sign-In failed.");
            } finally {
              setLoading(false);
            }
          },
          nonce: hashedNonce, // Provide hashed nonce to Google
          auto_select: false,
          itp_support: true,
          use_fedcm_for_prompt: true,
          context: mode === 'signup' ? 'signup' : 'signin'
        });

        // 4. Render the Button
        if (googleButtonRef.current) {
          window.google.accounts.id.renderButton(
            googleButtonRef.current,
            { 
              type: 'standard', 
              shape: 'pill', 
              theme: 'outline', 
              text: mode === 'signup' ? 'signup_with' : 'signin_with', 
              size: 'large', 
              logo_alignment: 'center', 
              width: 300 
            }
          );
        }
      } catch (e) {
        console.error("Error initializing Google Sign In", e);
      }
    };

    // Retry checking for window.google if script hasn't loaded yet
    const intervalId = setInterval(() => {
      if (window.google) {
        initializeGoogleSignIn();
        clearInterval(intervalId);
      }
    }, 100);

    return () => clearInterval(intervalId);
  }, [mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      if (mode === 'login') {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (error) throw error;

        // Manually fetch profile to ensure rapid redirection
        const profile = await api.getCurrentUser();
        if (profile) {
           onLogin(profile);
        } else {
           setTimeout(async () => {
              const retryProfile = await api.getCurrentUser();
              if (retryProfile) onLogin(retryProfile);
              else setError("Profile not found. Please contact support.");
           }, 2000);
        }

      } else {
        if (!phone) {
           setError("WhatsApp phone number is required.");
           setLoading(false);
           return;
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
            data: {
              name,
              role: UserRole.DENTIST,
              clinicName: clinicName,
              phone: phone
            }
          }
        });
        
        if (error) throw error;
        if (data.user && !data.session) {
          setNeedsVerification(true);
        } else if (data.session) {
           // Auto-login (if email confirm is disabled)
           const profile = await api.getCurrentUser();
           if(profile) onLogin(profile);
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (needsVerification) {
    return (
      <div className="min-h-screen bg-[#09090b] flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl p-8 shadow-2xl text-center animate-fade-in relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-green-500/5 to-transparent pointer-events-none"></div>
            <div className="w-16 h-16 bg-green-900/30 border border-green-500/20 rounded-full mx-auto flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(34,197,94,0.2)]">
              <Mail className="text-green-400" size={32} />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">Check your email</h2>
            <p className="text-zinc-400 mb-8 leading-relaxed">
              We've sent a verification link to <span className="text-white font-medium">{email}</span>.
            </p>

            <Button variant="secondary" onClick={() => { setNeedsVerification(false); onSwitchMode('login'); }} className="w-full">
              Back to Sign In
            </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col items-center justify-center p-4 relative overflow-hidden">
        {/* Ambient Background */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
           <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-blue-600/10 rounded-full blur-[120px]"></div>
           <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-purple-600/10 rounded-full blur-[120px]"></div>
        </div>

        <button onClick={onBack} className="absolute top-8 left-8 text-zinc-500 hover:text-white flex items-center gap-2 transition-colors z-20">
          <ArrowLeft size={20} /> Back
        </button>

        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-10 animate-slide-up">
            <div className="w-16 h-16 bg-white rounded-2xl mx-auto flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(255,255,255,0.1)]">
              <span className="text-black text-2xl font-bold">G</span>
            </div>
            <h2 className="text-4xl font-heading font-bold text-white mb-3 tracking-tight">
              {mode === 'login' ? 'Welcome Back' : 'Join the Network'}
            </h2>
            <p className="text-zinc-400">
              {mode === 'login' ? 'Sign in to access your digital lab' : 'Start your precision workflow today'}
            </p>
          </div>

          <div className="bg-zinc-900/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl animate-fade-in relative">
            {error && (
               <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center gap-3 text-red-400 text-sm">
                  <AlertCircle size={18}/> {error}
               </div>
            )}

            {/* Google Sign In Button Container */}
            <div className="mb-6 flex justify-center min-h-[44px]">
               <div ref={googleButtonRef} className="w-full flex justify-center"></div>
            </div>

            <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-zinc-800"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="bg-zinc-900 px-2 text-zinc-500">Or continue with email</span>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-2">
              {mode === 'signup' && (
                <div className="space-y-2 animate-fade-in">
                  <Input 
                    label="Full Name" 
                    placeholder="e.g. Dr. Jane Doe" 
                    value={name} 
                    onChange={(e: any) => setName(e.target.value)}
                    required 
                  />
                  <Input
                       label="Clinic Name"
                       placeholder="Smile Clinic"
                       value={clinicName}
                       onChange={(e: any) => setClinicName(e.target.value)}
                  />
                  <div className="relative">
                     <Input
                        label="WhatsApp Number"
                        type="tel"
                        placeholder="+20 1xxxxxxxxx"
                        value={phone}
                        onChange={(e: any) => setPhone(e.target.value)}
                        required
                     />
                     <Phone className="absolute right-4 top-[38px] text-zinc-400" size={18} />
                  </div>
                </div>
              )}
              
              <Input 
                label="Email Address" 
                type="email" 
                placeholder="doctor@clinic.com" 
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                required
              />
              <Input 
                label="Password" 
                type="password" 
                placeholder="••••••••" 
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                required
              />

              <div className="pt-4">
                <Button type="submit" className="w-full h-14 text-base shadow-[0_0_20px_rgba(255,255,255,0.15)] hover:shadow-[0_0_30px_rgba(255,255,255,0.25)] flex items-center justify-center gap-2" disabled={loading}>
                  {loading && <Loader2 className="animate-spin" size={20} />}
                  {loading ? (mode === 'login' ? 'Signing In...' : 'Creating Account...') : (mode === 'login' ? 'Sign In' : 'Create Account')}
                </Button>
              </div>
            </form>

            <div className="mt-8 pt-6 border-t border-white/5 text-center">
              <p className="text-zinc-500 text-sm">
                {mode === 'login' ? "New to Galal Lab? " : "Already have an account? "}
                <button 
                  onClick={() => onSwitchMode(mode === 'login' ? 'signup' : 'login')}
                  className="text-white font-semibold hover:underline decoration-blue-500 underline-offset-4"
                >
                  {mode === 'login' ? 'Create Account' : 'Log in'}
                </button>
              </p>
            </div>
          </div>
          
          <div className="mt-8 flex justify-center gap-6 text-zinc-600 text-xs">
             <span className="flex items-center gap-1"><ShieldCheck size={12}/> Secure 256-bit SSL</span>
             <span>•</span>
             <span>HIPAA Compliant</span>
          </div>
        </div>
    </div>
  );
};
